import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBillingDashboard, subscribePlan, cancelSubscription } from '../utils/api';

const BillingPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dashboard, setDashboard]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [upgrading, setUpgrading]   = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await getBillingDashboard();
      setDashboard(res.data.data);
    } catch {
      setError('Could not load billing information. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpgrade(planName) {
    setUpgrading(planName);
    try {
      const res = await subscribePlan(planName);
      const shortUrl = res.data?.data?.shortUrl;
      if (shortUrl) {
        window.open(shortUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Upgrade failed. Please try again.');
    } finally {
      setUpgrading('');
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      await cancelSubscription();
      setShowCancel(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Cancellation failed. Please try again.');
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return <div style={s.center}>Loading billing information…</div>;
  if (error)   return <div style={s.center}><p>{error}</p><button style={s.upgradeBtn} onClick={load}>Retry</button></div>;

  const { currentPlan, availablePlans } = dashboard;
  const isActive    = currentPlan.razorpaySubscriptionStatus === 'active';
  const isCancelled = currentPlan.razorpaySubscriptionStatus === 'cancelled';
  const validUntil  = currentPlan.subscriptionValidUntil
    ? new Date(currentPlan.subscriptionValidUntil).toLocaleDateString('en-IN')
    : null;
  const currentTier = availablePlans.find(p => p.planName === currentPlan.plan)?.tier ?? 0;

  return (
    <div style={s.page}>

      {/* ── Mobile header — shown on standalone /billing routes (not embedded in dashboard) ── */}
      {['/billing', '/solo/billing', '/owner/billing'].includes(location.pathname) && (
        <div style={s.mobileHeader}>
          <button
            style={s.backBtn}
            onClick={() =>
              location.key !== 'default'
                ? navigate(-1)
                : navigate(
                    location.pathname.startsWith('/owner')
                      ? '/owner'
                      : location.pathname.startsWith('/solo')
                        ? '/solo/dashboard'
                        : '/dashboard'
                  )
            }
            aria-label="Go back"
          >
            ←
          </button>
          <span style={s.mobileTitle}>Billing &amp; Plan</span>
          <div style={s.backBtn} />
        </div>
      )}

      {/* ── Current Plan Card ── */}
      <div style={s.card}>
        <h2 style={s.cardTitle}>Current Plan</h2>
        <div style={s.row}>
          <span style={s.planBadge}>{currentPlan.plan}</span>
          {isActive && (
            <span style={{ ...s.badge, background: '#e6f4ea', color: '#1a7340' }}>Active</span>
          )}
          {isCancelled && (
            <span style={{ ...s.badge, background: '#fdecea', color: '#c0392b' }}>Cancelled</span>
          )}
          {currentPlan.subscriptionPending && (
            <span style={{ ...s.badge, background: '#fff3e0', color: '#e65100' }}>Payment Pending</span>
          )}
        </div>

        {validUntil && (
          <p style={s.meta}>Access until: <strong>{validUntil}</strong></p>
        )}

        <div style={s.usageRow}>
          <span>
            Healers: <strong>{currentPlan.usageHealers ?? '—'}</strong>
            {' / '}
            {currentPlan.maxHealers != null ? currentPlan.maxHealers : '∞'}
          </span>
          <span style={{ marginLeft: 24 }}>
            Patients: <strong>{currentPlan.usagePatients ?? '—'}</strong>
            {' / '}
            {currentPlan.maxPatients != null ? currentPlan.maxPatients : '∞'}
          </span>
        </div>

        {isActive && (
          <button style={s.cancelBtn} onClick={() => setShowCancel(true)}>
            Cancel Subscription
          </button>
        )}
      </div>

      {/* ── Plan Table ── */}
      <h2 style={s.sectionTitle}>Available Plans</h2>
      <div style={s.planGrid}>
        {availablePlans.map(plan => {
          const isCurrent  = plan.planName === currentPlan.plan;
          const isUpgrade  = plan.tier > currentTier;
          return (
            <div key={plan.planName} style={{ ...s.planCard, ...(isCurrent ? s.planCardCurrent : {}) }}>
              <div style={s.planName}>{plan.planName}</div>
              <div style={s.planPrice}>
                {(plan.gstInclusivePrice ?? plan.monthlyPriceINR) != null ? `₹${plan.gstInclusivePrice ?? plan.monthlyPriceINR}/mo` : 'Free'}
              </div>
              <div style={s.planLimits}>
                {plan.maxHealers != null ? `Up to ${plan.maxHealers} healers` : 'Unlimited healers'}
              </div>
              <div style={s.planLimits}>
                {plan.maxPatients != null ? `Up to ${plan.maxPatients} patients` : 'Unlimited patients'}
              </div>

              {isCurrent ? (
                <div style={s.currentLabel}>Your current plan</div>
              ) : isUpgrade ? (
                <button
                  style={s.upgradeBtn}
                  disabled={!!upgrading}
                  onClick={() => handleUpgrade(plan.planName)}
                >
                  {upgrading === plan.planName ? 'Opening checkout…' : 'Upgrade'}
                </button>
              ) : (
                <div style={s.lowerLabel}>Lower than your current plan</div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Cancel Confirmation Modal ── */}
      {showCancel && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={{ marginTop: 0 }}>Cancel Subscription?</h3>
            <p style={{ color: '#555', lineHeight: 1.6 }}>
              Your access continues until the end of the current billing period.
              After that, your plan reverts to FREE and AI features will be disabled.
            </p>
            <div style={s.modalBtns}>
              <button style={s.upgradeBtn} onClick={() => setShowCancel(false)}>
                Keep Plan
              </button>
              <button style={s.cancelBtn} disabled={cancelling} onClick={handleCancel}>
                {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page:            { maxWidth: 900, margin: '0 auto', padding: 24 },
  center:          { textAlign: 'center', padding: 48, color: '#666' },
  card:            { background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' },
  cardTitle:       { marginTop: 0, fontSize: 18, fontWeight: 600, color: '#1a1a1a' },
  row:             { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  planBadge:       { fontSize: 22, fontWeight: 700, color: '#1a1a1a' },
  badge:           { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12 },
  meta:            { color: '#555', fontSize: 14, margin: '4px 0 12px' },
  usageRow:        { fontSize: 14, color: '#555', marginBottom: 16 },
  cancelBtn:       { background: '#fdecea', color: '#c0392b', border: 'none', borderRadius: 8, padding: '12px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  sectionTitle:    { fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#1a1a1a' },
  planGrid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 },
  planCard:        { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column', gap: 8 },
  planCardCurrent: { border: '2px solid #4a8f82' },
  planName:        { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  planPrice:       { fontSize: 22, fontWeight: 700, color: '#4a8f82' },
  planLimits:      { fontSize: 13, color: '#666' },
  currentLabel:    { fontSize: 13, color: '#4a8f82', fontWeight: 600, marginTop: 'auto', paddingTop: 8 },
  lowerLabel:      { fontSize: 12, color: '#888', marginTop: 'auto', paddingTop: 8 },
  upgradeBtn:      { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, marginTop: 'auto' },
  mobileHeader:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' },
  backBtn:         { minWidth: 44, minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  mobileTitle:     { fontSize: 16, fontWeight: 600, color: '#4a8f82' },
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:           { background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,.18)' },
  modalBtns:       { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 },
};

export default BillingPage;
